import { Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon, DocumentTextIcon, ExternalLinkIcon, FilterIcon, XIcon } from "@heroicons/react/outline";
import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";
import useSWRImmutable from "swr/immutable";
import { getData, OAuthError, postData, putData } from "../backend";
import { useModal, WorkflowContext } from "../contexts";
import useDebounce from "../hooks/useDebounce";
import useUser from "../hooks/useUser";
import { BLOCK_ICON_MAP } from "../utils";
import LoadingModal from "./LoadingModal";
import OAuthModalSalesforce from "./OAuthModalSalesforce";
import SmallSelectField from "./SmallSelectField";
import SmallTextInputField from "./SmallTextInputField";
import WorkflowModalSalesforceObjects, { SalesforceObject } from "./WorkflowModalSalesforceObjects";
import WorkflowSelectModal from "./WorkflowSelectModal";
import WorkflowSourceConditionModal from "./WorkflowSourceConditionModal";
import WorkflowSourceFieldTable from "./WorkflowSourceFieldTable";

const PAGE_SIZE = 100;

// SOQL does not support an OFFSET greater than 2000, so only the first pages of a large object can be browsed
const MAX_PAGES = Math.floor(2000 / PAGE_SIZE) + 1;

interface SalesforceColumn {
  name: string;
  label: string;
  type: string;
}

interface SalesforceRecords {
  columns: Array<SalesforceColumn>;
  results: Array<{ id: string, values: Array<any> }>;
  total: number;
}

function WorkflowSourceSalesforce() {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const { user, refreshUser, hasPermission } = useUser();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow, automationOptions, updateAutomationOptions } = workflowContext;

  const history = useHistory();
  const { url } = useRouteMatch();

  const { sourceSettingsPortalNode: portalTarget } = useContext(WorkflowContext);

  const source = workflow.source;
  const objectName = source?.salesforceConfig?.salesforceObjectName;
  const objectLabel = source?.salesforceConfig?.salesforceObjectLabel || objectName;
  const instanceUrl = user?.salesforceCredentials?.instanceUrl;
  const isConnected = !!user?.salesforceCredentials?.id;

  const [sortingField, setSortingField] = useState<string | undefined>(undefined);
  const [sortingDirection, setSortingDirection] = useState<"ASC" | "DSC">("ASC");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [currentPage, setCurrentPage] = useState(1);

  let recordsUrl = `/salesforce/objects/${objectName}/records/?search=${encodeURIComponent(debouncedSearch)}&limit=${PAGE_SIZE}&offset=${(currentPage - 1) * PAGE_SIZE}`;
  if (sortingField)
    recordsUrl += `&sorting=${sortingDirection === "DSC" ? "-" : ""}${sortingField}`;

  const { data, isLoading, error } = useSWRImmutable<SalesforceRecords>(isConnected && objectName ? recordsUrl : null, getData, { shouldRetryOnError: false });
  const { columns, results, total } = data ?? { columns: [], results: [], total: undefined };

  const [lastColumns, setLastColumns] = useState<Array<SalesforceColumn>>([]);
  const [lastTotal, setLastTotal] = useState<number | undefined>(undefined);
  const nPages = lastTotal ? Math.min(Math.ceil(lastTotal / PAGE_SIZE), MAX_PAGES) : 0;

  const columnWidth = {
    1: "w-full",
    2: "w-1/2",
    3: "w-1/3",
    4: "w-1/4",
    5: "w-1/5",
  }[lastColumns.length];

  useEffect(() => {
    setSearch("");
    setCurrentPage(1);
    setSortingField(undefined);
    setLastTotal(undefined);
    setLastColumns([]);
  }, [objectName]);

  useEffect(() => {
    if (total === undefined)
      return;

    setLastTotal(total);
    setLastColumns(columns);
  }, [total, columns]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  async function openSalesforceModal() {
    openModal(<LoadingModal />)

    postData("/salesforce/credentials/")
      .then(() => { refreshUser(); closeModal(); })
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModalSalesforce oauthUrl={error.oauthUrl} onClose={refreshUser} />);
        else
          console.error(error)
      });
  }

  function onSelectSalesforceObject(object: SalesforceObject) {
    openModal(<LoadingModal title={t("Updating Salesforce Object")} subtitle={t("Please wait while the object is updated...")} />);

    const updatedConfig = { ...source!.salesforceConfig!, salesforceObjectName: object.name, salesforceObjectLabel: object.label };
    putData(`/salesforce/config/${source!.salesforceConfig!.id}/`, updatedConfig)
      .then(() => {
        // Selected record IDs belong to the previous object
        updateAutomationOptions({ sourceItemIds: [], selectedTotal: 0 });
        mutateWorkflow();
        closeModal();
      })
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModalSalesforce oauthUrl={error.oauthUrl} onClose={() => { refreshUser(); closeModal(); }} />);
        else {
          console.error(error);
          closeModal();
        }
      });
  }

  function handleToggleRecordSelected(recordId: string) {
    const current = !!automationOptions.sourceItemIds?.includes(recordId);

    const sourceItemIds = current
      ? (automationOptions.sourceItemIds ?? []).filter(id => id !== recordId)
      : [...(automationOptions.sourceItemIds ?? []), recordId];

    updateAutomationOptions({ sourceItemIds, selectedTotal: sourceItemIds.length, mode: "SOURCE_ITEM_IDS" });
  }

  function handleSelectSortingField(field: string) {
    if (field === sortingField)
      setSortingDirection(sortingDirection === "ASC" ? "DSC" : "ASC");
    else
      setSortingDirection("ASC");

    setSortingField(field);
    setCurrentPage(1);
  }

  function formatValue(column: SalesforceColumn, value: any) {
    if (value === null || value === undefined || value === "")
      return "---";

    if (column.type === "date")
      return new Date(value).toLocaleDateString();

    if (column.type === "datetime")
      return new Date(value).toLocaleString();

    if (column.type === "boolean")
      return value ? t("Yes") : t("No");

    if (column.type === "currency")
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    if (column.type === "double" || column.type === "int")
      return Number(value).toLocaleString();

    if (column.type === "percent")
      return `${Number(value).toLocaleString()}%`;

    return String(value);
  }

  if (!source)
    return null;

  const showSourceOptions = workflow.documents.length > 0 || workflow.emails.length > 0;
  const needsConnection = !isConnected || error instanceof OAuthError;

  return (
    <>
      {portalTarget && hasPermission(workflow.team, "workflows.edit") &&
        createPortal(
          <>
            <div className="flex flex-col">
              <div className="font-semibold text-xs text-gray-400">
                {t("Selected Object Type")}
              </div>
              <div className="flex items-center gap-4">
                <div className="font-semibold text-gray-600">
                  {objectLabel}
                </div>
                <div className="text-xs underline cursor-pointer ml-auto" onClick={() => openModal(<WorkflowModalSalesforceObjects onSelect={onSelectSalesforceObject} />)}>
                  {t("Change")}
                </div>
              </div>
            </div>

            {showSourceOptions && <>
              <WorkflowSourceFieldTable workflow={workflow} mutateWorkflow={mutateWorkflow} disableCopy />

              <div className="flex flex-col">
                <div className="flex items-center font-gilroy font-semibold text-gray-600">
                  <FilterIcon className="w-4 h-4 mr-2" /> {t("Source Filter Conditions")}
                </div>
                <div className="flex items-center font-gilroy font-semibold text-sm text-gray-400">
                  {t("Only process data that meets certain conditions")}
                </div>
                {(source!.sourceConditions.length > 0) &&
                  <div className="flex items-center font-gilroy font-semibold text-sm text-gray-600">
                    <div><Trans i18nKey="Filtering by <bold>{{count}}</bold> condition" count={source!.sourceConditions.length} components={{ bold: <span className="font-semibold" /> }} /></div>
                  </div>
                }
                <button className="btn btn-white text-sm w-52 mt-2" onClick={() => openModal(<WorkflowSourceConditionModal workflow={workflow} mutateWorkflow={mutateWorkflow} />)}>
                  {source!.sourceConditions.length === 0 ? t("Add Conditions") : t("Edit Conditions")}
                </button>
              </div>
            </>}

            {!showSourceOptions && <>
              <div className="flex flex-col gap-4 justify-center">
                <div className="flex items-center">
                  <DocumentTextIcon className="w-6 h-6 mr-2" />
                  <div className="font-gilroy font-semibold text-lg">
                    {t("Connect a Template")}
                  </div>
                </div>
                <div className="flex flex-col gap-2 font-semibold text-gray-600">
                  {t("Create documents and presentations from a template")}
                </div>
                <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Template")} subtitle={t("Create or select a template to merge your data with")} showDocuments workflowContext={workflowContext} />)}>
                  {t("Connect")}
                </button>
              </div>
            </>}
          </>,
          portalTarget
        )
      }

      <Transition.Child className="flex flex-col bg-white w-full m-4 p-4 pt-2 shadow rounded"
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="flex w-full items-center">
          <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
            <img className="h-10 w-10 rounded-md" src={BLOCK_ICON_MAP["SALESFORCE"]} alt="Salesforce Icon" />
          </div>
          <div className="flex flex-col h-full justify-center">
            <div className="text-gray-400 font-gilroy font-semibold text-sm">
              {t("External Data Source")}
            </div>
            {instanceUrl
              ? <div className="flex items-center w-full font-gilroy font-semibold text-gray-600 cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(`${instanceUrl}/lightning/o/${objectName}/list`, "_blank")}>
                Salesforce {objectLabel}
                <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1" />
              </div>
              : <div className="font-gilroy font-semibold text-gray-600">
                Salesforce {objectLabel}
              </div>
            }
          </div>
          <XIcon className="w-6 h-6 ml-auto text-gray-400 text-xs hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        </div>

        {needsConnection &&
          <div className="flex flex-col gap-2 items-center justify-center h-full">
            <div className="font-semibold text-yellow">{t("Please allow Portant to access your data in Salesforce")}</div>
            <button className="btn btn-blue" onClick={openSalesforceModal}>
              {t("Connect Salesforce")}
            </button>
          </div>
        }

        {!needsConnection &&
          <div className="flex flex-col mt-2 overflow-hidden border border-gray-200 rounded h-full">
            {lastTotal !== undefined &&
              <>
                <div className="flex items-baseline px-4 py-2 pt-3 border-b">
                  <SmallTextInputField value={search} onChange={setSearch} placeholder={t("Search by name or record ID...")} className="w-80" disabled={lastTotal === 0 && search.length === 0} />
                  <div className="mr-auto flex-shrink" />

                  {(automationOptions.selectedTotal ?? 0) > 0 &&
                    <>
                      <div className="font-semibold text-blue text-sm ml-2">
                        {automationOptions.selectedTotal} {t("Selected")}
                      </div>
                      <div className="font-semibold text-gray-400 underline hover:text-gray-600 text-sm ml-2 cursor-pointer" onClick={() => updateAutomationOptions({ sourceItemIds: [], selectedTotal: 0 })}>
                        {t("Clear")}
                      </div>
                    </>
                  }
                  <div className="font-semibold text-gray-400 text-sm ml-auto">
                    {t("Total Results:")}
                  </div>
                  <div className="font-semibold text-gray-600 text-sm ml-2">
                    {lastTotal}
                  </div>
                  <div className="font-semibold text-gray-400 text-sm ml-4">
                    {t("Page:")}
                  </div>
                  <SmallSelectField value={String(currentPage)} onChange={value => setCurrentPage(Number(value))} className="ml-2 w-12" disabled={nPages <= 1}>
                    {Array.from({ length: (nPages || 1) }, (_, i) => i + 1).map(page => <option key={page} value={page}>{page}</option>)}
                  </SmallSelectField>
                </div>

                <div className="flex items-center gap-4 border-b border-gray-200 bg-gray-100 px-4 pt-2 pb-1 font-semibold text-sm text-gray-600 overflow-y-scroll flex-shrink-0 scrollbar-invisible">
                  <input type="checkbox" className="mr-4 invisible" disabled />
                  {lastColumns.map(column => (
                    <div key={column.name} className={`flex items-center ${columnWidth} cursor-pointer`} onClick={() => handleSelectSortingField(column.name)}>
                      {column.label}
                      {sortingField === column.name && sortingDirection === "ASC" &&
                        <ChevronDownIcon className="w-4 h-4 ml-1 -mt-1" />
                      }
                      {sortingField === column.name && sortingDirection === "DSC" &&
                        <ChevronUpIcon className="w-4 h-4 ml-1 -mt-1" />
                      }
                    </div>
                  ))}
                </div>
              </>
            }

            <div className="overflow-y-scroll h-full">
              {isLoading &&
                <div className="h-full flex items-center justify-center">
                  <img src="https://static.portant.co/portant-loading-gray.svg" className="w-24 h-24" alt="Loading Logo" />
                </div>
              }

              {(error && !isLoading) &&
                <div className="h-full flex items-center justify-center text-red font-semibold">
                  {t("Unable to fetch records from Salesforce, please try again.")}
                </div>
              }

              {total === 0 &&
                <div className="h-full flex items-center justify-center text-gray-400 font-semibold">
                  {t("No results were found")}
                </div>
              }

              {results.map(record => (
                <div key={record.id} className={`flex flex-col border-b last:border-0 px-4 py-4 pb-1 cursor-pointer ${automationOptions.sourceItemIds?.includes(record.id) ? "bg-blue-50" : "bg-white"}`} onClick={() => handleToggleRecordSelected(record.id)}>
                  <div className="flex items-center gap-4">
                    <input type="checkbox" className="mr-4" checked={!!automationOptions.sourceItemIds?.includes(record.id)} onChange={() => handleToggleRecordSelected(record.id)} onClick={e => e.stopPropagation()} />
                    <a className={`flex font-semibold text-gray-600 group hover:underline ${columnWidth} overflow-hidden flex-grow-0`} href={`${instanceUrl}/lightning/r/${objectName}/${record.id}/view`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                      <span className="truncate inline-block" style={{ "maxWidth": "calc(100% - 30px)" }}>
                        {record.values[0] ?? record.id}
                      </span>
                      <ExternalLinkIcon className="h-4 w-4 ml-1 text-blue invisible group-hover:visible inline-block" />
                    </a>
                    {columns.slice(1).map((column, i) => (
                      <div key={column.name} className={`${columnWidth} truncate`}>
                        {formatValue(column, record.values[i + 1])}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        }
      </Transition.Child>
    </>
  );

}

export default WorkflowSourceSalesforce;
